import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDebounce } from '@uidotdev/usehooks'
import { AxiosError } from 'axios'
import { ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { z } from 'zod'

import { Toast } from '@/common/components/Toast'
import { OrderBy, useOrderBy } from '@/common/hooks/useOrderBy'
import { accountModel } from '@/common/models/account'
import { applicationModel } from '@/common/models/application'
import { applicationAccountUserModel } from '@/common/models/applicationAccountUser'
import { personModel } from '@/common/models/person'
import { userModel } from '@/common/models/user'
import { userPreferenceModel } from '@/common/models/userPreference'
import { calculateItemsPerPage } from '@/common/utils/calculateItemsPerPage'
import { api, ApiError, APIPaginated, ApiWhere } from '@/lib/axios'
import { FilterUsersAccessForm } from '../schemas/FilterUserApplicationAccountUserSchema'
import { useFilterApplicationAccountUser } from './useFilterApplicationAccountUser'

export const userApplicationAccountUserPaginatedDTO = userModel.extend({
  Person: personModel,
  UserPreference: userPreferenceModel,
  ApplicationAccountUsers: z.array(
    applicationAccountUserModel.extend({
      Account: accountModel,
      Application: applicationModel,
    }),
  ),
})

export type UserApplicationAccountUserPaginated = z.infer<
  typeof userApplicationAccountUserPaginatedDTO
>

export type GetPaginateUserApplicationAccountUser = {
  page: number
  orderBy: OrderBy
  filters: FilterUsersAccessForm
}

async function getPaginateUserApplicationAccountUser(
  props: GetPaginateUserApplicationAccountUser,
) {
  const { filters } = props

  const where: ApiWhere = {
    isActive: true,
    ...(filters.userType && { userType: filters.userType }),
    ...(filters.gender && {
      Person: { genre: filters.gender },
    }),
    ...(filters.birthMonth && {
      Person: { birthMonth: filters.birthMonth },
    }),
    ...(filters.birthYear && {
      Person: { birthYear: filters.birthYear },
    }),
    ...(filters.startCreated &&
      filters.endCreated && {
        createdAt: {
          gte: filters.startCreated,
          lte: filters.endCreated,
        },
      }),
    ...(filters.freeSearch && {
      AND: [
        {
          OR: [
            { email: { contains: filters.freeSearch } },
            { Person: { name: { contains: filters.freeSearch } } },
            { Person: { documentNumber: { contains: filters.freeSearch } } },
            {
              UserPreference: { displayName: { contains: filters.freeSearch } },
            },
          ],
        },
      ],
    }),
  }

  const { data } = await api.backend.get<
    APIPaginated<UserApplicationAccountUserPaginated>
  >('/user/paginate', {
    params: {
      page: props.page,
      limit: 25,
      relations: {
        orderBy: props.orderBy,
        where,
        include: {
          Person: true,
          UserPreference: true,
          ApplicationAccountUsers: {
            where: { isActive: true },
            include: {
              Account: true,
              Application: true,
            },
          },
        },
      },
    },
  })

  return data
}

export const usePaginateUserApplicationAccountUser = () => {
  const [tableSearch, setTableSearch] = useState('')
  const [tableSearchResult, setTableSearchResult] = useState<
    UserApplicationAccountUserPaginated[]
  >([])
  const [page, setPage] = useState(1)
  const [filters] = useFilterApplicationAccountUser()

  const { t } = useTranslation(['common', 'sso'])
  const debouncedTableSearch = useDebounce(tableSearch, 300)
  const { orderBy, handleChangeOrder } = useOrderBy({
    UserPreference: { displayName: 'asc' },
  })
  const { data, isLoading, error } = useQuery<
    APIPaginated<UserApplicationAccountUserPaginated>,
    AxiosError<ApiError>
  >({
    queryKey: [
      '/users-access/paginate',
      page,
      orderBy,
      ...Object.values(filters),
    ],
    queryFn: () =>
      getPaginateUserApplicationAccountUser({ page, orderBy, filters }),
  })

  const handleUsersAccessSearch = (search: string) => {
    setTableSearch(search?.toLowerCase() || '')
  }

  const handleChangePage = (page: number) => {
    setPage(page)
  }

  useEffect(() => {
    if (!error) return

    toast(Toast, {
      data: {
        type: 'danger',
        icon: ServerCrash,
        title: t('sso:access.paginate_table.toast_error.title'),
        description:
          error?.response?.data?.friendlyMessage ||
          t('sso:access.paginate_table.toast_error.description'),
      },
    })
  }, [error, t])

  useEffect(() => {
    if (!data || data?.items?.length === 0) return

    const filteredData = data.items.filter(
      (user) =>
        user.email.includes(debouncedTableSearch) ||
        user.UserPreference.displayName.includes(debouncedTableSearch),
    )

    setTableSearchResult(filteredData)
  }, [data, debouncedTableSearch, t])

  return {
    userApplicationAccountUser: debouncedTableSearch
      ? tableSearchResult
      : data?.items || [],
    userApplicationAccountUserPaginated: data?.meta,
    isLoadingUserApplicationAccountUser: isLoading,
    userApplicationAccountUserOrderBy: orderBy,
    userApplicationAccountUserPerPage: calculateItemsPerPage(data?.meta),
    filtersQty: Object.values(filters).filter(Boolean).length,
    handleUsersAccessSearch,
    handleChangePage,
    handleChangeOrder,
  }
}
